import { Add, CategoryRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Alert, Avatar, CircularProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { setGroupSearchValue, setGroupSelectedItem } from 'app/actions/geofence';
import { IconButton, InputSearch, Scrollbar } from 'app/components';
import { GeofenceGroupDialog, GeofenceGroupForm, GeofenceGroupList } from 'app/pages/GeofenceGroup';
import { useAsyncAction } from 'app/services';
import { geofenceGroupDelete, geofenceGroupLoad, geofenceGroupUpdate } from 'app/store/geofence';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const GeofenceGroup = function () {
  const formRef = useRef();
  const dispatch = useDispatch();
  const { groupItems, groupSelectedItem, groupSearchValue } = useSelector((state) => state.geofence);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { asyncAction: fetchData, isLoading: isFetchLoading } = useAsyncAction(geofenceGroupLoad);
  const {
    asyncAction: updateSubmit,
    isLoading: isUpdateLoading,
    isSuccessful: isUpdateSuccessful,
    isError: isUpdateError,
  } = useAsyncAction(geofenceGroupUpdate, [groupSelectedItem]);
  const {
    asyncAction: deleteSubmit,
    isLoading: isDeleteLoading,
    isSuccessful: isDeleteSuccessful,
    isError: isDeleteError,
  } = useAsyncAction(geofenceGroupDelete, [groupSelectedItem]);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleFormClick = () => {
    formRef.current?.handleFormSubmit();
  };

  const handleGeofenceSearchChange = (value) => dispatch(setGroupSearchValue(value));

  const handleFormSubmit = (params) => {
    updateSubmit(groupSelectedItem.id, params);
  };

  // eslint-disable-next-line no-unused-vars
  const handleDeleteClick = () => {
    deleteSubmit(groupSelectedItem.id);
  };

  useEffect(() => {
    fetchData();

    return () => dispatch(setGroupSelectedItem(null));
  }, []);

  return (
    <React.Fragment>
      <Stack
        direction='column'
        sx={(theme) => ({
          width: '500px',
          height: '100%',
          zIndex: '1010',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: theme.palette.divider,
        })}>
        {!isFetchLoading && groupItems.length && (
          <React.Fragment>
            <Stack
              direction='row'
              alignItems='center'
              sx={(theme) => ({
                padding: theme.spacing(2),
              })}>
              <InputSearch placeholder='Название группы геозон' value={groupSearchValue} onChange={handleGeofenceSearchChange} />
              <IconButton onClick={handleDialogOpen} endIcon={<Add />} />
              <GeofenceGroupDialog open={dialogOpen} onClose={handleDialogClose} />
            </Stack>
            <Stack
              direction='column'
              alignItems='center'
              justifyContent='center'
              sx={{
                flex: '1 1 auto',
                overflow: 'hidden',
              }}>
              <GeofenceGroupList />
            </Stack>
          </React.Fragment>
        )}
        {!isFetchLoading && !groupItems.length && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={{
              width: '100%',
              height: '100%',
            }}>
            <Typography align='center' variant='body1' component='div'>
              Здесь пока ничего нет
            </Typography>
            <Typography align='center' color='text.secondary' variant='caption' component='div'>
              В этом списке будут отображаться группы геозон
            </Typography>
          </Stack>
        )}
        {isFetchLoading && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={{
              width: '100%',
              height: '100%',
            }}>
            <CircularProgress size={24} />
          </Stack>
        )}
      </Stack>
      <Stack
        direction='column'
        sx={{
          height: '100%',
          flex: '1 1 auto',
        }}>
        {groupSelectedItem && (
          <Scrollbar>
            <Stack
              direction='column'
              alignItems='center'
              sx={(theme) => ({
                maxWidth: '540px',
                margin: 'auto',
                width: '100%',
                padding: theme.spacing(2),
              })}>
              <Stack
                direction='column'
                alignItems='center'
                sx={(theme) => ({
                  marginBottom: theme.spacing(3),
                  '.MuiAvatar-root': {
                    width: '56px',
                    height: '56px',
                    bgcolor: theme.palette.primary.main,
                  },
                  '.MuiAvatar-circular': {
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                  },
                  '.MuiTypography-root:first-of-type': {
                    marginTop: theme.spacing(1),
                  },
                })}>
                <Avatar>
                  <CategoryRounded />
                </Avatar>
                <Typography variant='body1'>{groupSelectedItem.name}</Typography>
              </Stack>
              <Stack
                direction='column'
                alignItems='center'
                sx={(theme) => ({
                  width: '100%',
                  position: 'relative',
                  padding: theme.spacing(2),
                  border: '1px solid',
                  borderRadius: theme.shape.borderRadius,
                  borderColor: theme.palette.divider,
                  bgcolor: 'background.paper',
                  form: {
                    width: '100%',
                  },
                })}>
                <Stack
                  direction='column'
                  sx={(theme) => ({
                    width: '100%',
                    paddingBottom: theme.spacing(1),
                    '.MuiTypography-h6': {
                      fontSize: '15px',
                    },
                    '.MuiTypography-caption': {
                      color: theme.palette.text.secondary,
                    },
                  })}>
                  <Typography variant='h6'>Информация</Typography>
                  <Typography variant='caption'>Основная информация группы геозон</Typography>
                </Stack>
                <GeofenceGroupForm ref={formRef} onSubmit={handleFormSubmit} defaultValues={groupSelectedItem} />
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    '.MuiAlert-root': {
                      width: '100%',
                      marginTop: theme.spacing(2),
                      borderRadius: theme.shape.borderRadius,
                    },
                  })}>
                  {isUpdateError && (
                    <Alert severity='error'>
                      Произошла ошибка при изменении данных. Попробуйте снова или обратитесь в техническую поддержку.
                    </Alert>
                  )}
                  {isUpdateSuccessful && <Alert severity='success'>Данные успешно изменены.</Alert>}
                </Stack>
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    paddingTop: theme.spacing(3),
                  })}>
                  <LoadingButton variant='contained' loading={isUpdateLoading} onClick={handleFormClick} disableElevation>
                    Сохранить изменения
                  </LoadingButton>
                </Stack>
              </Stack>
              <Stack
                direction='column'
                alignItems='center'
                sx={(theme) => ({
                  width: '100%',
                  display: 'none',
                  position: 'relative',
                  marginTop: theme.spacing(3),
                  padding: theme.spacing(2),
                  border: '1px solid',
                  borderRadius: theme.shape.borderRadius,
                  borderColor: theme.palette.divider,
                  bgcolor: 'background.paper',
                  '.MuiAlert-root': {
                    marginTop: theme.spacing(0),
                    marginBottom: theme.spacing(3),
                  },
                })}>
                <Stack
                  direction='column'
                  sx={(theme) => ({
                    width: '100%',
                    paddingBottom: theme.spacing(3),
                    '.MuiTypography-h6': {
                      fontSize: '15px',
                    },
                    '.MuiTypography-caption': {
                      color: theme.palette.text.secondary,
                    },
                  })}>
                  <Typography variant='h6'>Удалить группу</Typography>
                  <Typography variant='caption'>
                    Можно удалить данную группу геозон, но это действие необратимо. Все прикрепленные геозоны будут удалены из данной
                    группы. Пожалуйста, будьте уверены, что Вы хотите это сделать.
                  </Typography>
                </Stack>
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    '.MuiAlert-root': {
                      width: '100%',
                      marginTop: theme.spacing(1),
                      borderRadius: theme.shape.borderRadius,
                    },
                  })}>
                  {isDeleteError && (
                    <Alert severity='error'>
                      Произошла ошибка при удалении данных. Попробуйте снова или обратитесь в техническую поддержку.
                    </Alert>
                  )}
                  {isDeleteSuccessful && <Alert severity='success'>Данные успешно удалены.</Alert>}
                </Stack>
                <Stack
                  direction='row'
                  justifyContent='flex-end'
                  sx={(theme) => ({
                    width: '100%',
                    paddingTop: theme.spacing(0),
                  })}>
                  <LoadingButton color='error' variant='contained' loading={isDeleteLoading} disableElevation>
                    Удалить группу
                  </LoadingButton>
                </Stack>
              </Stack>
            </Stack>
          </Scrollbar>
        )}
        {!groupSelectedItem && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={(theme) => ({
              height: '100%',
              padding: theme.spacing(0, 2),
              '.MuiTypography-root': {
                borderRadius: '20px',
                marginTop: '24px',
                padding: theme.spacing(0.5, 2),
                bgcolor: alpha(theme.palette.grey.main, 0.12),
              },
            })}>
            <Typography align='center' color='text.secondary' variant='caption' component='label'>
              Выберите группу геозон из списка
            </Typography>
          </Stack>
        )}
      </Stack>
    </React.Fragment>
  );
};

export default GeofenceGroup;
